import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiAdminGetPartner, apiAgentGetProfile, type AdminPartner } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { CodeBlock } from '@/components/ui/code-block'
import { useT } from '@/contexts/language'

const NEXBIT_URL = (import.meta.env.VITE_NEXBIT_URL as string | undefined) ?? 'http://localhost:3001'


function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-foreground mt-6 mb-2 first:mt-0">
      {children}
    </h3>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="font-mono text-xs text-muted-foreground leading-relaxed">{children}</p>
}

function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded-sm border border-border text-foreground">
      {children}
    </code>
  )
}

const ENDPOINT_PATHS = [
  '/api/wallet/balance',
  '/api/wallet/debit',
  '/api/wallet/credit',
  '/api/wallet/rollback',
  '/api/wallet/settle',
] as const

const ENDPOINT_METHODS: Record<string, string> = {
  '/api/wallet/balance': 'GET',
  '/api/wallet/debit': 'POST',
  '/api/wallet/credit': 'POST',
  '/api/wallet/rollback': 'POST',
  '/api/wallet/settle': 'POST',
}

function methodColor(m: string) {
  if (m === 'GET') return 'text-success'
  if (m === 'POST') return 'text-primary'
  return 'text-muted-foreground'
}

function buildTsExample(partner: AdminPartner, nexbitUrl: string) {
  return `// TypeScript / Node.js — Express example
import express from 'express'
import { randomBytes, createHmac } from 'crypto'
const app = express()
app.use(express.json())

const NEXBIT_API_KEY  = process.env.NEXBIT_API_KEY  // "${partner.nexbitApiKeyMasked}..."
const LAUNCH_SECRET   = process.env.LAUNCH_SECRET   // given separately by Lotmatic — never expose client-side
const WALLET_API_KEY  = process.env.WALLET_API_KEY  // your secret key
const PARTNER_ID      = '${partner.id}'

function verifyApiKey(req, res, next) {
  if (req.headers['x-api-key'] !== WALLET_API_KEY) {
    return res.status(401).json({ code: 'UNAUTHORIZED', message: 'Invalid API key' })
  }
  next()
}

app.get('/api/wallet/balance', verifyApiKey, (req, res) => {
  const { memberId } = req.query
  const balance = '1000.00'
  res.json({ memberId, username: 'member_username', balance, currency: 'THB' })
})

app.post('/api/wallet/debit', verifyApiKey, (req, res) => {
  const { txId, memberId, amount, gameCode, roundId } = req.body
  res.json({ txId, status: 'success', balance: '900.00', currency: 'THB' })
})

app.post('/api/wallet/credit', verifyApiKey, (req, res) => {
  const { txId, memberId, amount, gameCode, roundId, betTxId } = req.body
  res.json({ txId, status: 'success', balance: '1900.00', currency: 'THB' })
})

app.post('/api/wallet/rollback', verifyApiKey, (req, res) => {
  const { txId, originalTxId, memberId } = req.body
  res.json({ txId, status: 'success', balance: '1000.00', currency: 'THB' })
})

// Reconciliation only — does not move money (debit/credit already did)
app.post('/api/wallet/settle', verifyApiKey, (req, res) => {
  const { txId, memberId, result, betTxId } = req.body
  res.json({ txId, status: 'success', balance: '900.00', currency: 'THB' })
})

app.listen(3000, () => console.log('Wallet server running'))

// Step 0 — register a new member with Lotmatic (do this once, e.g. on signup).
// Idempotent: calling again with the same username returns the same memberId.
// Keep using YOUR OWN username (not the returned memberId) in launchMember()
// below and in every wallet call Lotmatic makes to you — memberId is Lotmatic's
// own reference id, returned so you can cross-check on your side.
async function registerMember(username: string, displayName: string) {
  const res = await fetch('${nexbitUrl}/api/external/create-member', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Partner-Key': NEXBIT_API_KEY },
    body: JSON.stringify({ partnerId: PARTNER_ID, username, displayName }),
  })
  if (!res.ok) throw new Error(\`Create member failed: \${res.status}\`)
  const { memberId } = await res.json()
  return memberId
}

// Step 1 — generate the launchToken yourself: <nonce>.<hmac>
function generateLaunchToken(externalMemberId: string): string {
  const nonce = randomBytes(16).toString('hex')
  const hmac = createHmac('sha256', LAUNCH_SECRET)
    .update(\`\${PARTNER_ID}:\${externalMemberId}:\${nonce}\`)
    .digest('hex')
  return \`\${nonce}.\${hmac}\`
}

// Step 2 — exchange the launchToken for a Lotmatic session
async function launchMember(externalMemberId: string) {
  const launchToken = generateLaunchToken(externalMemberId)

  const res = await fetch('${nexbitUrl}/api/external/launch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Partner-Key': NEXBIT_API_KEY },
    body: JSON.stringify({ launchToken, partnerId: PARTNER_ID, memberId: externalMemberId }),
  })
  if (!res.ok) throw new Error(\`Launch failed: \${res.status}\`)

  const { accessToken, expiresIn, launchUrl, member } = await res.json()
  return launchUrl // redirect the member here
}`
}

function buildPhpExample(nexbitUrl: string, partnerId: string) {
  return `<?php
$walletApiKey  = getenv('WALLET_API_KEY');
$nexbitApiKey  = getenv('NEXBIT_API_KEY');
$launchSecret  = getenv('LAUNCH_SECRET'); // given separately by Lotmatic — never expose client-side
$partnerId     = '${partnerId}';

function verifyApiKey(): void {
    global $walletApiKey;
    $key = $_SERVER['HTTP_X_API_KEY'] ?? '';
    if ($key !== $walletApiKey) {
        http_response_code(401);
        echo json_encode(['code' => 'UNAUTHORIZED', 'message' => 'Invalid API key']);
        exit;
    }
}

$method = $_SERVER['REQUEST_METHOD'];
$path   = parse_url($_SERVER['REQUEST_URI'], PHP_URL_PATH);
header('Content-Type: application/json');
verifyApiKey();

if ($method === 'GET' && $path === '/api/wallet/balance') {
    $memberId = $_GET['memberId'] ?? '';
    echo json_encode(['memberId' => $memberId, 'username' => 'member_username', 'balance' => '1000.00', 'currency' => 'THB']);
} elseif ($method === 'POST' && $path === '/api/wallet/debit') {
    $body = json_decode(file_get_contents('php://input'), true);
    echo json_encode(['txId' => $body['txId'], 'status' => 'success', 'balance' => '900.00', 'currency' => 'THB']);
} elseif ($method === 'POST' && $path === '/api/wallet/credit') {
    $body = json_decode(file_get_contents('php://input'), true);
    echo json_encode(['txId' => $body['txId'], 'status' => 'success', 'balance' => '1900.00', 'currency' => 'THB']);
} elseif ($method === 'POST' && $path === '/api/wallet/rollback') {
    $body = json_decode(file_get_contents('php://input'), true);
    echo json_encode(['txId' => $body['txId'], 'status' => 'success', 'balance' => '1000.00', 'currency' => 'THB']);
} elseif ($method === 'POST' && $path === '/api/wallet/settle') {
    // Reconciliation only — does not move money (debit/credit already did)
    $body = json_decode(file_get_contents('php://input'), true);
    echo json_encode(['txId' => $body['txId'], 'status' => 'success', 'balance' => '900.00', 'currency' => 'THB']);
}

// ── Member provisioning ─────────────────────────────────────────────────────
// Register once (e.g. on signup). Idempotent — same username returns same memberId.
// Keep using YOUR OWN username below, not the returned memberId (that's Lotmatic's
// own reference id for your records).
function register_member(string $username, string $displayName): string {
    global $nexbitApiKey, $partnerId;
    $ch = curl_init('${nexbitUrl}/api/external/create-member');
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json', "X-Partner-Key: {$nexbitApiKey}"]);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(['partnerId' => $partnerId, 'username' => $username, 'displayName' => $displayName]));
    $response = json_decode(curl_exec($ch), true);
    curl_close($ch);
    return $response['memberId'];
}

// ── Member launch ───────────────────────────────────────────────────────────
function generate_launch_token(string $partnerId, string $externalMemberId, string $launchSecret): string {
    $nonce = bin2hex(random_bytes(16));
    $hmac  = hash_hmac('sha256', "{$partnerId}:{$externalMemberId}:{$nonce}", $launchSecret);
    return "{$nonce}.{$hmac}";
}

function launch_member(string $externalMemberId): string {
    global $nexbitApiKey, $launchSecret, $partnerId;
    $launchToken = generate_launch_token($partnerId, $externalMemberId, $launchSecret);

    $ch = curl_init('${nexbitUrl}/api/external/launch');
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json', "X-Partner-Key: {$nexbitApiKey}"]);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
        'launchToken' => $launchToken,
        'partnerId'   => $partnerId,
        'memberId'    => $externalMemberId,
    ]));
    $response = json_decode(curl_exec($ch), true);
    curl_close($ch);

    return $response['launchUrl']; // redirect the member here
}`
}

function buildPythonExample(nexbitUrl: string, partnerId: string) {
  return `# Python (Flask) example
from flask import Flask, request, jsonify, abort
import os, secrets, hmac, hashlib, requests

app = Flask(__name__)
WALLET_API_KEY = os.environ['WALLET_API_KEY']
NEXBIT_API_KEY = os.environ['NEXBIT_API_KEY']
LAUNCH_SECRET  = os.environ['LAUNCH_SECRET']  # given separately by Lotmatic — never expose client-side
PARTNER_ID     = '${partnerId}'
NEXBIT_URL     = '${nexbitUrl}'

def verify():
    if request.headers.get('X-Api-Key') != WALLET_API_KEY:
        abort(401, 'Invalid API key')

@app.get('/api/wallet/balance')
def balance():
    verify()
    return jsonify(memberId=request.args.get('memberId', ''), username='member_username', balance='1000.00', currency='THB')

@app.post('/api/wallet/debit')
def debit():
    verify()
    body = request.json
    return jsonify(txId=body['txId'], status='success', balance='900.00', currency='THB')

@app.post('/api/wallet/credit')
def credit():
    verify()
    body = request.json
    return jsonify(txId=body['txId'], status='success', balance='1900.00', currency='THB')

@app.post('/api/wallet/rollback')
def rollback():
    verify()
    body = request.json
    return jsonify(txId=body['txId'], status='success', balance='1000.00', currency='THB')

# Reconciliation only — does not move money (debit/credit already did)
@app.post('/api/wallet/settle')
def settle():
    verify()
    body = request.json
    return jsonify(txId=body['txId'], status='success', balance='900.00', currency='THB')

# Register once (e.g. on signup). Idempotent — same username returns same memberId.
# Keep using YOUR OWN username below, not the returned memberId (Lotmatic's own
# reference id, kept for your records only).
def register_member(username: str, display_name: str) -> str:
    res = requests.post(f'{NEXBIT_URL}/api/external/create-member',
        headers={'X-Partner-Key': NEXBIT_API_KEY},
        json={'partnerId': PARTNER_ID, 'username': username, 'displayName': display_name})
    res.raise_for_status()
    return res.json()['memberId']

def generate_launch_token(external_member_id: str) -> str:
    nonce = secrets.token_hex(16)
    payload = f'{PARTNER_ID}:{external_member_id}:{nonce}'
    digest = hmac.new(LAUNCH_SECRET.encode(), payload.encode(), hashlib.sha256).hexdigest()
    return f'{nonce}.{digest}'

def launch_member(external_member_id: str) -> str:
    launch_token = generate_launch_token(external_member_id)
    res = requests.post(f'{NEXBIT_URL}/api/external/launch',
        headers={'X-Partner-Key': NEXBIT_API_KEY},
        json={'launchToken': launch_token, 'partnerId': PARTNER_ID, 'memberId': external_member_id})
    res.raise_for_status()
    return res.json()['launchUrl']  # redirect the member here

if __name__ == '__main__':
    app.run(port=3000)`
}

export default function IntegrationDocsPage() {
  const { id } = useParams<{ id: string }>()
  const t = useT()
  const [partner, setPartner] = useState<AdminPartner | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const data = id ? await apiAdminGetPartner(id) : await apiAgentGetProfile()
      setPartner(data)
    } catch {
      setError('Failed to load partner')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  if (loading) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full rounded-[var(--radius)]" />
      </div>
    )
  }

  if (error || !partner) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-destructive/10" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error ?? 'Not found'}</p>
        </div>
      </div>
    )
  }

  const epDescs = [t.docs.epBalance, t.docs.epDebit, t.docs.epCredit, t.docs.epRollback, t.docs.epSettle]

  const flowSteps = [
    t.docs.flowStep0,
    t.docs.flowStep1,
    t.docs.flowStep2,
    t.docs.flowStep3,
    t.docs.flowStep4,
    t.docs.flowStep5,
    t.docs.flowStep6,
    t.docs.flowStep7,
    t.docs.flowStep8,
  ]

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        {id && (
          <>
            <Button variant="ghost" size="sm" asChild className="font-mono text-xs gap-1.5 text-muted-foreground">
              <Link to={`/admin/partners/${id}`}>
                <ArrowLeft className="h-3.5 w-3.5" />
                {partner.name}
              </Link>
            </Button>
            <span className="text-border">·</span>
          </>
        )}
        <h1 className="font-mono text-sm font-bold">{t.docs.title}</h1>
        <Badge variant="outline" className="font-mono text-[10px] h-4 px-1.5 ml-auto">
          {partner.walletType}
        </Badge>
      </div>

      <div className="flex flex-col gap-6">
        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.overview}</span>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <P>{t.docs.overviewText}</P>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                [t.docs.agentCode,   partner.code],
                [t.docs.yourApiKey,  `${partner.nexbitApiKeyMasked}…`],
                [t.docs.apiBase,     `${NEXBIT_URL}/api`],
                [t.docs.walletUrl,   partner.walletUrl ?? '—'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-[var(--radius-sm)] border border-border p-3 flex flex-col gap-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
                  <code className="font-mono text-xs text-foreground break-all">{value}</code>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.integrationFlow}</span>
          </div>
          <div className="p-4 flex flex-col gap-3">
            {flowSteps.map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-mono text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="font-mono text-xs text-muted-foreground leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.createMemberTitle}</span>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <P>{t.docs.createMemberExplain}</P>
            <div className="rounded-[var(--radius-sm)] border border-border bg-secondary/20 p-3">
              <p className="font-mono text-xs text-muted-foreground leading-relaxed">{t.docs.createMemberIdempotentNote}</p>
            </div>

            <div>
              <SectionHeading>{t.docs.createMemberRequestHeading}</SectionHeading>
              <CodeBlock
                lang={`POST ${NEXBIT_URL}/api/external/create-member`}
                code={`// Headers\nX-Partner-Key: ${partner.nexbitApiKeyMasked}...\nContent-Type: application/json\n\n// Body\n{\n  "partnerId":   "${partner.id}",\n  "username":    "pm-006",\n  "displayName": "John Doe"\n}`}
              />
            </div>

            <div>
              <SectionHeading>{t.docs.createMemberResponseHeading}</SectionHeading>
              <CodeBlock
                lang="json — Response 201 (new) / 200 (already existed)"
                code={`{\n  "memberId": "f1edd3fb-153e-4e29-a3d4-52ae3383ac41"\n}`}
              />
            </div>
          </div>
        </section>

        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.launchTitle}</span>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <P>{t.docs.launchExplain}</P>
            <div className="rounded-[var(--radius-sm)] border border-warning/40 bg-warning/5 p-3 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-warning leading-relaxed">{t.docs.launchSecretNote}</p>
            </div>
            <P>{t.docs.launchTokenFormat}</P>

            <div>
              <SectionHeading>{t.docs.launchGenerateHeading}</SectionHeading>
              <CodeBlock
                lang="typescript"
                code={`import { randomBytes, createHmac } from 'crypto'\n\n// partnerId is bound into the signed payload — your Launch Secret only signs\n// tokens for YOUR partnerId, even though every agent uses the same algorithm.\nfunction generateLaunchToken(partnerId: string, externalMemberId: string, launchSecret: string): string {\n  const nonce = randomBytes(16).toString('hex')\n  const hmac = createHmac('sha256', launchSecret)\n    .update(\`\${partnerId}:\${externalMemberId}:\${nonce}\`)\n    .digest('hex')\n  return \`\${nonce}.\${hmac}\`\n}`}
              />
            </div>

            <div>
              <SectionHeading>{t.docs.launchRequestHeading}</SectionHeading>
              <CodeBlock
                lang={`POST ${NEXBIT_URL}/api/external/launch`}
                code={`// Headers\nX-Partner-Key: ${partner.nexbitApiKeyMasked}...\nContent-Type: application/json\n\n// Body\n{\n  "launchToken": "a1b2c3d4e5f60718293a4b5c6d7e8f90.9f8e7d6c5b4a3928...",\n  "partnerId":   "${partner.id}",\n  "memberId":    "pm-001"\n}`}
              />
            </div>

            <div>
              <SectionHeading>{t.docs.launchResponseHeading}</SectionHeading>
              <CodeBlock
                lang="json — Response 200"
                code={`{\n  "accessToken": "eyJhbGciOiJIUzI1NiIs...",\n  "expiresIn":   3600,\n  "launchUrl":   "${(import.meta.env.VITE_GAME_APP_URL as string | undefined) ?? 'http://localhost:3000'}/launch?token=eyJhbGciOiJIUzI1NiIs...",\n  "member": {\n    "id":       "8f14e45f-ceea-4...",\n    "username": "${partner.code.slice(0, 8)}_pm-001"\n  }\n}`}
              />
            </div>
          </div>
        </section>

        {/* KENO: same token, same members, same Wallet API — only the endpoint differs. */}
        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.kenoLaunchTitle}</span>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <P>{t.docs.kenoLaunchExplain}</P>
            <CodeBlock
              lang={`POST ${NEXBIT_URL}/api/external/keno/launch`}
              code={`// Headers and body: identical to /api/external/launch\nX-Partner-Key: ${partner.nexbitApiKeyMasked}...\nContent-Type: application/json\n\n{\n  "launchToken": "a1b2c3d4e5f60718293a4b5c6d7e8f90.9f8e7d6c5b4a3928...",\n  "partnerId":   "${partner.id}",\n  "memberId":    "pm-001"\n}\n\n// Response 200 — launchUrl now points at KENO\n{\n  "launchUrl": "${(import.meta.env.VITE_KENO_APP_URL as string | undefined) ?? 'http://localhost:5105'}/launch?token=eyJhbGciOiJIUzI1NiIs...",\n  ...\n}`}
            />
            <div className="rounded-[var(--radius-sm)] border border-warning/40 bg-warning/5 p-3 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-warning leading-relaxed">{t.docs.kenoRefundNote}</p>
            </div>
          </div>
        </section>

        {/* Draw lotteries: same protocol, but money moves per bill, and money back is asynchronous. */}
        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.drawLotteryTitle}</span>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <P>{t.docs.drawLotteryExplain}</P>
            <div className="rounded-[var(--radius-sm)] border border-warning/40 bg-warning/5 p-3 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-warning leading-relaxed">{t.docs.drawLotteryMoneyNote}</p>
            </div>
          </div>
        </section>

        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.docs.walletApi}
            </span>
          </div>
          <div className="p-4 flex flex-col gap-5">
            <div>
              <SectionHeading>{t.docs.authSection}</SectionHeading>
              {(() => {
                const [preKey, afterKey = ''] = t.docs.authText.split('X-Api-Key: <your_wallet_api_key>')
                const [pre401, post401 = ''] = afterKey.split('401')
                return (
                  <P>
                    {preKey}<InlineCode>X-Api-Key: &lt;your_wallet_api_key&gt;</InlineCode>
                    {pre401}<InlineCode>401</InlineCode>{post401}
                  </P>
                )
              })()}
            </div>
            <Separator />
            <div>
              <SectionHeading>{t.docs.endpointsSection}</SectionHeading>
              <div className="rounded-[var(--radius-sm)] border border-border overflow-hidden">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-border bg-secondary/40">
                      <th className="text-left px-3 py-2 text-muted-foreground font-semibold">{t.docs.method}</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-semibold">{t.docs.path}</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-semibold hidden sm:table-cell">{t.docs.description}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ENDPOINT_PATHS.map((path, i) => (
                      <tr key={path} className="border-b border-border last:border-0">
                        <td className={cn('px-3 py-2.5 font-bold', methodColor(ENDPOINT_METHODS[path] ?? 'GET'))}>{ENDPOINT_METHODS[path]}</td>
                        <td className="px-3 py-2.5 text-foreground">{path}</td>
                        <td className="px-3 py-2.5 text-muted-foreground hidden sm:table-cell">{epDescs[i]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <Separator />
            <div>
              <SectionHeading>GET /api/wallet/balance</SectionHeading>
              {(() => {
                const [before, after = ''] = t.docs.balanceQueryParam.split('memberId')
                return <P>{before}<InlineCode>memberId</InlineCode>{after}</P>
              })()}
              <div className="mt-2">
                <CodeBlock lang="json — Response 200" code={`{\n  "memberId": "pm-001",\n  "username": "john_doe",\n  "balance": "1000.00",\n  "currency": "THB"\n}`} />
              </div>
            </div>
            <div>
              <SectionHeading>POST /api/wallet/debit</SectionHeading>
              {(() => {
                const [before, after = ''] = t.docs.debitIdempotent.split('txId')
                return <P>{before}<InlineCode>txId</InlineCode>{after}</P>
              })()}
              <div className="mt-2 flex flex-col gap-2">
                <CodeBlock lang="json — Request body" code={`{\n  "txId":     "nexbit-debit-uuid",\n  "memberId": "pm-001",\n  "gameCode": "CRYPTO",\n  "roundId":  "round-uuid",\n  "amount":   "100.00",\n  "currency": "THB"\n}`} />
                <CodeBlock lang="json — Response 200" code={`{\n  "txId":     "nexbit-debit-uuid",\n  "status":   "success",\n  "balance":  "900.00",\n  "currency": "THB"\n}`} />
              </div>
            </div>
            <div>
              <SectionHeading>POST /api/wallet/rollback</SectionHeading>
              <P>{t.docs.rollbackDesc}</P>
              <div className="mt-2">
                <CodeBlock lang="json — Request body" code={`{\n  "txId":         "nexbit-rollback-uuid",\n  "originalTxId": "nexbit-debit-uuid",\n  "memberId":     "pm-001",\n  "reason":       "settlement_failed"\n}`} />
              </div>
            </div>
            <div>
              <SectionHeading>POST /api/wallet/settle</SectionHeading>
              <P>{t.docs.settleDesc}</P>
              <div className="mt-2 flex flex-col gap-2">
                <CodeBlock lang="json — Request body (won)" code={`{\n  "txId":      "nexbit-settle-uuid",\n  "memberId":  "pm-001",\n  "gameCode":  "CRYPTO",\n  "roundId":   "round-uuid",\n  "betTxId":   "nexbit-debit-uuid",\n  "result":    "won",\n  "amount":    "100.00",\n  "winAmount": "190.00",\n  "currency":  "THB"\n}`} />
                <CodeBlock lang="json — Request body (lost)" code={`{\n  "txId":     "nexbit-settle-uuid",\n  "memberId": "pm-001",\n  "gameCode": "CRYPTO",\n  "roundId":  "round-uuid",\n  "betTxId":  "nexbit-debit-uuid",\n  "result":   "lost",\n  "amount":   "100.00",\n  "currency": "THB"\n}`} />
                <CodeBlock lang="json — Response 200" code={`{\n  "txId":     "nexbit-settle-uuid",\n  "status":   "success",\n  "balance":  "900.00",\n  "currency": "THB"\n}`} />
              </div>
            </div>
          </div>
        </section>

        {/* What the integration tester checks. Reading this first is the difference
            between passing on the first run and a round of back-and-forth. */}
        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Integration checklist / เกณฑ์ที่ชุดทดสอบใช้ตัดสิน
            </span>
          </div>
          <div className="p-4">
            <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
              Lotmatic runs these checks against your endpoints before go-live. Each row is one
              case; matching all of them means a clean report.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-1.5 pr-3 font-medium">Situation</th>
                    <th className="py-1.5 font-medium">Expected response</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-[11px]">
                  {[
                    ['GET /balance — known member', '200 + { memberId, username, balance, currency }'],
                    ['GET /balance — unknown member', '404 + { "code": "MEMBER_NOT_FOUND" }'],
                    ['Any endpoint — wrong X-Api-Key', '401 (never 200)'],
                    ['POST /debit — normal', '200, balance reduced by exactly amount'],
                    ['POST /debit — same txId again', 'same result, balance unchanged (or 409)'],
                    ['POST /debit — amount > balance', '422 + { "code": "INSUFFICIENT_BALANCE" }, balance unchanged'],
                    ['POST /debit — negative amount', '400 + { "code": "INVALID_AMOUNT" }, balance unchanged'],
                    ['POST /credit — normal', '200, balance increased by exactly amount'],
                    ['POST /credit — same txId again', 'balance unchanged'],
                    ['POST /rollback — known txId', '200, balance back to pre-debit value'],
                    ['POST /rollback — unknown originalTxId', '404 + { "code": "ORIGINAL_TX_NOT_FOUND" }'],
                    ['POST /settle — any result', '200, balance unchanged (notification only)'],
                    ['All requests', 'respond within 8 seconds, Content-Type: application/json'],
                  ].map(([situation, expected]) => (
                    <tr key={situation} className="border-b border-border/60">
                      <td className="py-1.5 pr-3">{situation}</td>
                      <td className="py-1.5">{expected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Test traffic carries <code className="font-mono">X-Nexbit-Test: 1</code> and a
              <code className="font-mono"> note</code> starting with
              <code className="font-mono"> [NEXBIT-TEST]</code>, so you can filter it out of your
              own reporting.
            </p>
          </div>
        </section>

        <section className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.docs.codeExamples}</span>
          </div>
          <div className="p-4">
            <Tabs defaultValue="ts">
              <TabsList className="mb-4">
                <TabsTrigger value="ts" className="font-mono text-xs">TypeScript</TabsTrigger>
                <TabsTrigger value="php" className="font-mono text-xs">PHP</TabsTrigger>
                <TabsTrigger value="python" className="font-mono text-xs">Python</TabsTrigger>
              </TabsList>
              <TabsContent value="ts">
                <CodeBlock lang="typescript" code={buildTsExample(partner, NEXBIT_URL)} />
              </TabsContent>
              <TabsContent value="php">
                <CodeBlock lang="php" code={buildPhpExample(NEXBIT_URL, partner.id)} />
              </TabsContent>
              <TabsContent value="python">
                <CodeBlock lang="python" code={buildPythonExample(NEXBIT_URL, partner.id)} />
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </div>
    </div>
  )
}
